import {
    PropsWithChildren,
    useContext,
    createContext,
    useEffect,
    useState,
    useCallback,
    useRef,
} from "react"
import {
    CheckoutCustomer,
    initializePaddle,
    Paddle,
    PaddleEventData,
} from "@paddle/paddle-js"

type PaddleCheckoutContextProps = {
    checkout: (
        paddlePriceId: string,
        customer?: CheckoutCustomer,
        customData?: Record<string, unknown>
    ) => Promise<PaddleEventData>
    checkoutWithTransaction: (
        transactionId: string,
        customer?: CheckoutCustomer,
        customData?: Record<string, unknown>
    ) => Promise<PaddleEventData>
}

const PaddleCheckoutContext = createContext<
    PaddleCheckoutContextProps | undefined
>(undefined)

export function PaddleCheckoutContextProvider({ children }: PropsWithChildren) {
    const parentCtx = useContext(PaddleCheckoutContext)

    const [paddle, setPaddle] = useState<Paddle>()
    const openCheckoutResolveRef = useRef<((value: any) => void) | null>(null)
    const openCheckoutRejectRef = useRef<((reason?: any) => void) | null>(null)
    // Lets openCheckout() wait out an in-flight initializePaddle() call instead of rejecting outright —
    // without this, a checkout click that lands before the Paddle CDN script has finished loading always
    // fails with "Paddle not initialized" even though the very next click, once it's ready, works fine.
    const paddleInitRef = useRef<Promise<Paddle | undefined>>()

    // Download and initialize Paddle instance from CDN
    useEffect(() => {
        // Nested provider: an outer provider already initialized Paddle.
        // Skip re-initializing to avoid double-registering the client token.
        if (parentCtx) return
        const token = import.meta.env.VITE_PADDLE_CLIENT_SIDE_TOKEN
        const environment =
            import.meta.env.VITE_PADDLE_SANDBOX === true ||
            import.meta.env.VITE_PADDLE_SANDBOX === "true" ||
            import.meta.env.VITE_PADDLE_SANDBOX === 1 ||
            import.meta.env.VITE_PADDLE_SANDBOX === "1"
                ? "sandbox"
                : "production"
        paddleInitRef.current = initializePaddle({
            environment: environment,
            token: token,
            checkout: {
                settings: {
                    theme: "dark",
                    variant: "one-page",
                },
            },
            eventCallback: (data) => {
                if (data.name === "checkout.completed") {
                    openCheckoutResolveRef.current?.(data)
                    openCheckoutResolveRef.current = null
                    openCheckoutRejectRef.current = null
                }

                if (data.name === "checkout.closed") {
                    openCheckoutResolveRef.current?.(data)
                    openCheckoutResolveRef.current = null
                    openCheckoutRejectRef.current = null
                }

                if (data.name === "checkout.error") {
                    openCheckoutRejectRef.current?.(data.detail)
                    openCheckoutResolveRef.current = null
                    openCheckoutRejectRef.current = null
                }
            },
        }).then((paddleInstance: Paddle | undefined) => {
            if (paddleInstance) {
                setPaddle(paddleInstance)
            }
            return paddleInstance
        })
    }, [])

    const openCheckout = useCallback(
        (open: (paddle: Paddle) => void, validationError?: string) => {
            if (validationError) return Promise.reject(validationError)
            if (openCheckoutResolveRef.current)
                return Promise.reject("Checkout already in progress")

            const runOpen = (paddleInstance: Paddle | undefined) =>
                new Promise<PaddleEventData>(
                    (
                        resolve: (data: PaddleEventData) => void,
                        reject: (errorMsg: string) => void
                    ) => {
                        if (!paddleInstance) {
                            reject("Paddle not initialized")
                            return
                        }
                        openCheckoutResolveRef.current = resolve
                        openCheckoutRejectRef.current = reject
                        open(paddleInstance)
                    }
                )

            // Already ready: open synchronously (same behaviour as before this fix). Only defer to the
            // in-flight initializePaddle() promise when a click genuinely arrives before it's resolved.
            return paddle
                ? runOpen(paddle)
                : (
                      paddleInitRef.current ?? Promise.resolve(undefined)
                  ).then(runOpen)
        },
        [paddle]
    )

    const checkout: (
        paddlePriceId: string,
        customer?: CheckoutCustomer,
        customData?: Record<string, unknown>
    ) => Promise<PaddleEventData> = useCallback(
        (
            paddlePriceId: string,
            customer?: CheckoutCustomer,
            customData?: Record<string, unknown>
        ) =>
            openCheckout(
                (paddle) =>
                    paddle.Checkout.open({
                        items: [
                            {
                                priceId: paddlePriceId,
                                quantity: 1,
                            },
                        ],
                        customer,
                        customData,
                    }),
                !paddlePriceId
                    ? "Error opening checkout: Invalid price id"
                    : undefined
            ),
        [openCheckout]
    )

    const checkoutWithTransaction: (
        transactionId: string,
        customer?: CheckoutCustomer,
        customData?: Record<string, unknown>
    ) => Promise<PaddleEventData> = useCallback(
        (
            transactionId: string,
            customer?: CheckoutCustomer,
            customData?: Record<string, unknown>
        ) =>
            openCheckout(
                (paddle) =>
                    paddle.Checkout.open({
                        transactionId,
                        customer,
                        customData,
                    }),
                !transactionId
                    ? "Error opening checkout: Invalid transaction id"
                    : undefined
            ),
        [openCheckout]
    )

    // Nested provider: defer entirely to the outer provider's context value
    // rather than shadowing it with one whose Paddle instance never initializes.
    if (parentCtx) return <>{children}</>

    return (
        <PaddleCheckoutContext.Provider
            value={{ checkout, checkoutWithTransaction }}
        >
            {children}
        </PaddleCheckoutContext.Provider>
    )
}

export function usePaddleCheckoutContext(): PaddleCheckoutContextProps {
    const ctx = useContext(PaddleCheckoutContext)
    if (ctx === undefined) throw Error("Paddle checkout context is undefined")
    return ctx
}

export default PaddleCheckoutContext
